import React, {useState, useEffect, useMemo} from 'react';
import {useIntl} from 'react-intl';
import {ForecastTheme, tokensForThemeProvider} from '@forecast-it/design-system';
import {NOVA_PURPLE, TEXT_MAIN, ERROR_COLOR, BG_DISABLED, TEXT_DISABLED_GRAY} from './colors';
import ForecastTooltip from '../../../forecast-app/shared/components/tooltips/ForecastTooltip';
import Util from '../../../forecast-app/shared/util/util';
import type {ProjectProposal, PlaceholderProgress} from './types';
import {deriveNovaFilledFields} from './constants';
import {
	ReviewFormPanel,
	ReviewFormScroll,
	SowBanner,
	SowBannerViewSource,
	FormSectionHeader,
	SowFieldLabel,
	NovaFieldWrapper,
	NovaIndicatorBadge,
	ReviewFooter,
	FieldGroup,
	FieldInput,
	DateRow,
	DateFieldGroup,
	BudgetPillRow,
	BudgetPill,
	DeliverableRow,
	DeliverableColorDot,
	DeliverableName,
	DeliverableMeta,
	ClientDropdownList,
	ClientDropdownItem,
	ClientDropdownChevron,
	PlaceholderRow,
	PlaceholderRoleName,
	PlaceholderStatusLabel,
} from './styles';

const {
	sys: {
		color: {id: _idColors},
	},
} = tokensForThemeProvider[ForecastTheme.Sky];
const DELIVERABLE_COLORS = [_idColors['9'], _idColors['7'], _idColors['11'], _idColors['2'], _idColors['5']];

interface SowReviewFormPanelProps {
	proposal: ProjectProposal;
	novaFilledFields: string[];
	sowFilename: string | null;
	missingRequiredFields?: string[];
	createPlaceholdersEnabled?: boolean;
	onCreatePlaceholdersChange?: (enabled: boolean) => void;
	onProposalChange: (proposal: ProjectProposal) => void;
	onFieldUserEdited?: (field: string) => void;
	onViewSource?: () => void;
	clients?: {id: string; name: string}[];
	persons?: {id: string; backendId: number; fullName: string}[];
	placeholderProgresses?: PlaceholderProgress[];
	isBlocked?: boolean;
}

// Fields the backend can Nova-fill that correspond to visible form fields.
// rate_card_id is backend-only and excluded — it would inflate novaCount above totalFields.
// budget is excluded for NON_BILLABLE projects (field is hidden), handled via totalFields.
const NOVA_FORM_FIELDS = new Set(['name', 'client_id', 'start_date', 'end_date', 'budget_type', 'budget', 'deliverables']);

const SowReviewFormPanel: React.FC<SowReviewFormPanelProps> = ({
	proposal,
	novaFilledFields,
	sowFilename,
	missingRequiredFields = [],
	createPlaceholdersEnabled = false,
	onCreatePlaceholdersChange,
	onProposalChange,
	onFieldUserEdited,
	onViewSource,
	clients = [],
	persons = [],
	placeholderProgresses,
	isBlocked = false,
}) => {
	const {formatMessage} = useIntl();
	const update = <K extends keyof ProjectProposal>(field: K, value: ProjectProposal[K]) =>
		onProposalChange({...proposal, [field]: value});
	// When backend omits nova_filled_fields, derive from proposal's non-null values so
	// the count is always accurate (covers reconciliation phase and session-restore paths).
	// When the SOW is blocked (retainer/MSA), Nova never ran extraction so nothing counts
	// as Nova-filled — the proposal holds only defaults set before the block fired.
	const effectiveNovaFields = React.useMemo(() => {
		if (isBlocked) return [];
		if (novaFilledFields.length > 0) return novaFilledFields;
		return deriveNovaFilledFields(proposal);
	}, [isBlocked, novaFilledFields, proposal]);
	const isNova = (field: string) => effectiveNovaFields.includes(field);

	// client_name may hold a raw name Nova extracted from the SoW that doesn't match any
	// real Forecast client — client_id stays null until the user (or reconciliation) picks
	// one. Only ever show client_name once client_id is actually resolved, so the search
	// box never displays an unresolved guess as if it were a confirmed selection.
	const resolvedClientName = proposal.client_id != null ? proposal.client_name ?? '' : '';

	const [clientSearch, setClientSearch] = useState(resolvedClientName);
	const [showClientDropdown, setShowClientDropdown] = useState(false);
	const [clientUserIsSearching, setClientUserIsSearching] = useState(false);
	const filteredClients = useMemo(
		() =>
			clientUserIsSearching ? clients.filter(c => c.name.toLowerCase().includes(clientSearch.toLowerCase())) : clients,
		[clientUserIsSearching, clients, clientSearch]
	);

	useEffect(() => {
		setClientSearch(resolvedClientName);
	}, [resolvedClientName]);

	const [ownerSearch, setOwnerSearch] = useState(proposal.owner_name ?? '');
	const [showOwnerDropdown, setShowOwnerDropdown] = useState(false);
	const [ownerUserIsSearching, setOwnerUserIsSearching] = useState(false);
	const filteredPersons = useMemo(
		() =>
			ownerUserIsSearching ? persons.filter(p => p.fullName.toLowerCase().includes(ownerSearch.toLowerCase())) : persons,
		[ownerUserIsSearching, persons, ownerSearch]
	);

	useEffect(() => {
		setOwnerSearch(proposal.owner_name ?? '');
	}, [proposal.owner_name]);

	const novaCount = effectiveNovaFields.filter(f => NOVA_FORM_FIELDS.has(f)).length;
	const totalFields =
		5 + // name, client_id, start_date, end_date, budget_type
		(proposal.budget_type === 'FIXED_PRICE' ? 1 : 0) + // budget amount (FIXED_PRICE only)
		(proposal.deliverables?.length ? 1 : 0); // deliverables section

	return (
		<ReviewFormPanel>
			<ReviewFormScroll>
				<SowBanner>
					<span aria-hidden="true" style={{color: NOVA_PURPLE, flexShrink: 0}}>
						✦
					</span>
					<span>
						{formatMessage(
							{id: 'sow_review.nova_prefilled_banner'},
							{filename: <em key="fn">{sowFilename ?? 'your SoW'}</em>}
						)}
					</span>
					{onViewSource && (
						<SowBannerViewSource onClick={onViewSource}>
							{formatMessage({id: 'sow_review.view_source'})}
						</SowBannerViewSource>
					)}
				</SowBanner>

				<FormSectionHeader>{formatMessage({id: 'sow_review.section_basics'})}</FormSectionHeader>

				<FieldGroup>
					<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_project_name'})}</SowFieldLabel>
					<NovaFieldWrapper>
						<FieldInput
							type="text"
							value={proposal.name}
							onChange={e => {
								onFieldUserEdited?.('name');
								update('name', e.target.value);
							}}
						/>
						{isNova('name') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
					</NovaFieldWrapper>
				</FieldGroup>

				<DateRow>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'sow_review.field_client'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								type="text"
								value={clientSearch}
								placeholder="Search clients…"
								style={{paddingRight: 28, cursor: 'pointer'}}
								onChange={e => {
									setClientSearch(e.target.value);
									setClientUserIsSearching(true);
									setShowClientDropdown(true);
								}}
								onFocus={() => {
									setClientUserIsSearching(false);
									setShowClientDropdown(true);
								}}
								onBlur={() =>
									setTimeout(() => {
										setShowClientDropdown(false);
										// Snap back to last valid selection — prevents a stale client_id
										// remaining in the proposal when the user types then blurs without picking.
										setClientSearch(resolvedClientName);
									}, 150)
								}
							/>
							{/* Nova badge and chevron share the same right:10px slot — only one renders at a time */}
							{isNova('client_id') ? (
								<NovaIndicatorBadge>✦</NovaIndicatorBadge>
							) : (
								<ClientDropdownChevron>▾</ClientDropdownChevron>
							)}
							{showClientDropdown && filteredClients.length > 0 && (
								<ClientDropdownList>
									{filteredClients.map(c => (
										<ClientDropdownItem
											key={c.id}
											onMouseDown={() => {
												const clientId = Util.getIdFromBase64String(c.id);
												if (!isNaN(clientId)) {
													onFieldUserEdited?.('client_id');
													onFieldUserEdited?.('client_name');
													onProposalChange({
														...proposal,
														client_id: clientId,
														client_name: c.name,
													});
												}
												setClientSearch(c.name);
												setClientUserIsSearching(false);
												setShowClientDropdown(false);
											}}
										>
											{c.name}
										</ClientDropdownItem>
									))}
								</ClientDropdownList>
							)}
						</NovaFieldWrapper>
					</DateFieldGroup>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'sow_review.field_project_owner'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								type="text"
								value={ownerSearch}
								placeholder={formatMessage({id: 'sow_review.field_project_owner_placeholder'})}
								style={{paddingRight: 28, cursor: 'pointer'}}
								onChange={e => {
									setOwnerSearch(e.target.value);
									setOwnerUserIsSearching(true);
									setShowOwnerDropdown(true);
									// Mark as user-edited when they clear the field — prevents
									// bot proposals from silently re-inserting the viewer identity.
									if (!e.target.value) onFieldUserEdited?.('owner_person_id');
								}}
								onFocus={() => {
									setOwnerUserIsSearching(false);
									setShowOwnerDropdown(true);
								}}
								onBlur={() =>
									setTimeout(() => {
										setShowOwnerDropdown(false);
										setOwnerSearch(proposal.owner_name ?? '');
									}, 150)
								}
							/>
							<ClientDropdownChevron>▾</ClientDropdownChevron>
							{showOwnerDropdown && filteredPersons.length > 0 && (
								<ClientDropdownList>
									{filteredPersons.map(p => (
										<ClientDropdownItem
											key={p.id}
											onMouseDown={() => {
												onFieldUserEdited?.('owner_person_id');
												onProposalChange({
													...proposal,
													owner_person_id: p.backendId,
													owner_name: p.fullName,
												});
												setOwnerSearch(p.fullName);
												setOwnerUserIsSearching(false);
												setShowOwnerDropdown(false);
											}}
										>
											{p.fullName}
										</ClientDropdownItem>
									))}
								</ClientDropdownList>
							)}
						</NovaFieldWrapper>
					</DateFieldGroup>
				</DateRow>

				<DateRow>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_start_date'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								type="date"
								value={proposal.start_date ?? ''}
								onChange={e => {
									onFieldUserEdited?.('start_date');
									update('start_date', e.target.value);
								}}
							/>
							{isNova('start_date') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
						</NovaFieldWrapper>
					</DateFieldGroup>
					<DateFieldGroup>
						<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_end_date'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								type="date"
								value={proposal.end_date ?? ''}
								onChange={e => {
									onFieldUserEdited?.('end_date');
									update('end_date', e.target.value);
								}}
							/>
							{isNova('end_date') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
						</NovaFieldWrapper>
					</DateFieldGroup>
				</DateRow>

				{(proposal.deliverables?.length ?? 0) > 0 && (
					<>
						<FormSectionHeader>
							{formatMessage({id: 'sow_review.section_deliverables'}, {count: proposal.deliverables.length})}
						</FormSectionHeader>
						{proposal.deliverables.map((d, i) => (
							<DeliverableRow key={d.name + i}>
								<DeliverableColorDot color={DELIVERABLE_COLORS[i % DELIVERABLE_COLORS.length]} />
								<DeliverableName>{d.name}</DeliverableName>
								<DeliverableMeta>
									{d.start_date && d.end_date ? `${d.start_date} – ${d.end_date}` : ''}
									{d.total_hours ? ` · ${d.total_hours} hrs` : ''}
								</DeliverableMeta>
							</DeliverableRow>
						))}
					</>
				)}

				{(placeholderProgresses?.length ?? 0) > 0 && (
					<>
						<FormSectionHeader>
							{formatMessage(
								{id: 'sow_review.section_placeholders'},
								{count: (placeholderProgresses ?? []).length}
							)}
						</FormSectionHeader>
						{(placeholderProgresses ?? []).map((p, i) => (
							<PlaceholderRow key={p.name + i}>
								<PlaceholderRoleName>{p.name}</PlaceholderRoleName>
								<PlaceholderStatusLabel status={p.status}>
									{p.status === 'done'
										? formatMessage({id: 'sow_review.placeholder_status_done'})
										: p.status === 'failed'
										? formatMessage({id: 'sow_review.placeholder_status_failed'})
										: p.status === 'creating'
										? formatMessage({id: 'sow_review.placeholder_status_creating'})
										: formatMessage({id: 'sow_review.placeholder_status_pending'})}
								</PlaceholderStatusLabel>
							</PlaceholderRow>
						))}
					</>
				)}

				<FormSectionHeader>{formatMessage({id: 'sow_review.section_budget'})}</FormSectionHeader>

				<FieldGroup>
					<SowFieldLabel>{formatMessage({id: 'ai_project_planning.field_budget_type'})}</SowFieldLabel>
					<BudgetPillRow>
						{[
							{key: 'FIXED_PRICE', labelId: 'ai_project_planning.budget_fixed_price', isDisabled: false},
							{
								key: 'TIME_AND_MATERIALS',
								labelId: 'ai_project_planning.budget_time_materials',
								isDisabled: false,
							},
							{key: 'NON_BILLABLE', labelId: 'ai_project_planning.budget_non_billable', isDisabled: true},
							{key: 'RETAINER', labelId: 'ai_project_planning.budget_retainer', isDisabled: true},
						].map(({key, labelId, isDisabled}) => (
							<ForecastTooltip
								key={key}
								content={
									isDisabled
										? formatMessage({
												id:
													key === 'NON_BILLABLE'
														? 'ai_project_planning.budget_non_billable_disabled_tooltip'
														: 'ai_project_planning.budget_retainer_disabled_tooltip',
										  })
										: undefined
								}
							>
								<BudgetPill
									selected={!isDisabled && proposal.budget_type === key}
									disabled={isDisabled}
									style={
										isDisabled
											? {
													cursor: 'not-allowed',
													opacity: 0.5,
													background: BG_DISABLED,
													color: TEXT_DISABLED_GRAY,
													pointerEvents: 'none',
											  }
											: undefined
									}
									onClick={
										isDisabled
											? undefined
											: () => {
													onFieldUserEdited?.('budget_type');
													onProposalChange({
														...proposal,
														budget_type: key,
													});
											  }
									}
								>
									{formatMessage({id: labelId})}
								</BudgetPill>
							</ForecastTooltip>
						))}
					</BudgetPillRow>
				</FieldGroup>

				{proposal.budget_type === 'FIXED_PRICE' && (
					<FieldGroup>
						<SowFieldLabel>{formatMessage({id: 'sow_review.field_budget_amount'})}</SowFieldLabel>
						<NovaFieldWrapper>
							<FieldInput
								type="text"
								inputMode="decimal"
								value={proposal.budget ?? ''}
								placeholder="0"
								onChange={e => {
									onFieldUserEdited?.('budget');
									const raw = e.target.value;
									if (raw === '' || raw === '-') {
										update('budget', null);
									} else {
										const n = Number(raw.replace(/,/g, ''));
										if (!isNaN(n) && n >= 0) update('budget', n);
									}
								}}
							/>
							{isNova('budget') && <NovaIndicatorBadge>✦</NovaIndicatorBadge>}
						</NovaFieldWrapper>
					</FieldGroup>
				)}

				<FieldGroup>
					<label
						style={{
							display: 'flex',
							alignItems: 'center',
							gap: 8,
							cursor: 'pointer',
							fontSize: 13,
							color: TEXT_MAIN,
							userSelect: 'none',
						}}
					>
						<input
							type="checkbox"
							checked={createPlaceholdersEnabled}
							onChange={e => onCreatePlaceholdersChange?.(e.target.checked)}
							style={{accentColor: NOVA_PURPLE, width: 14, height: 14, flexShrink: 0}}
						/>
						{formatMessage({id: 'sow_review.create_placeholders_toggle'})}
					</label>
				</FieldGroup>
			</ReviewFormScroll>

			<ReviewFooter>
				<span style={{color: NOVA_PURPLE, fontWeight: 600}}>✦ Nova</span>{' '}
				{formatMessage({id: 'sow_review.footer_filled_rest'}, {filled: novaCount, total: totalFields})}
				{missingRequiredFields.length > 0 && (
					<span style={{color: ERROR_COLOR, marginLeft: 8}}>
						{'· '}
						{missingRequiredFields.join(', ')} still required
					</span>
				)}
			</ReviewFooter>
		</ReviewFormPanel>
	);
};

export default SowReviewFormPanel;
